import React, { useEffect, useState, FC } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { gql, useMutation, useQuery } from '@apollo/client';
import * as types from "../gql/graphql";

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';

import Autocomplete from '@mui/material/Autocomplete';
import Card from "@mui/material/Card";

import "primereact/resources/themes/md-light-indigo/theme.css";

import MDBox from './MDBox/MDBoxRoot';
import MDButton from './MDButton/MDButtonRoot';
import MDInput from './MDInput/MDInputRoot';
import MDTypography from './MDTypography/MDTypographyRoot';
import { useScreenSize } from "../hooks/ScreenSizeContext";



// Add student to the event 
// Remove WL if needed
// TODO: Don't check capacity limit (when passed parameter 'staff' and checked permissions)
const addSignupQuery = gql(`
mutation AddSignupForEvent($eventId: BigInt!, $regId: BigInt!, $addToWL: Boolean!) {
    add_signup(
      p_event_id: $eventId, 
      p_registration_id: $regId,
      p_add_to_wl: $addToWL,
      p_staff: true
    ) 
}
`);

// Remove student from event signup or WL
const deleteSignupQuery = gql(`
mutation deleteSignupInEventQuery($id:BigInt!) {
    delete_signup(p_id: $id)
}
`);

const searchStudentQuery = gql(`
query SearchStudent($st: String!, $cl: BigInt!) {
    get_available_regs_for_event(p_class: $cl, p_search: $st) {
      edges {
        node {
          id
          fs_purchases {
            id
            fs_students {
              FirstName
              LastName
              Email
              id
            }
          }
        }
      }
    }
  }
 `);



interface EditEventAttendeesProps {
    event: types.Fs_Events;
    attendies: types.Fs_SignupEdge[];
}

const EditEventAttendees: FC<EditEventAttendeesProps> = ({ event, attendies }) => {
    type EventFormType = types.Fs_Events & { day: Date, time: Date };
    const isSmallScreen = useScreenSize();

    const [isSelectStudentOpen, setIsSelectStudentOpen] = useState(false);
    const [isConfirmOverCapacityOpen, setIsConfirmOverCapacityOpen] = useState(false);
    const [addingToWL, setAddingToWL] = useState(false);

    const [studentInputValue, setStudentInputValue] = useState('');


    const signedStudents = attendies?.filter(item => item.node.status === 0 || item.node.status === 1);
    const WLStudents = attendies?.filter(item => item.node.status === 2); // 2 - wait list

    const { data: dataSelectStudent, error: errorSelectStudent, loading: loadingSelectStudent, refetch: refetchSelectStudent } = useQuery(searchStudentQuery, { variables: { st: "", cl: event.class } });

    const [addSignupMutation, { data: add_data, loading: add_loading, error: add_error, reset: addReset }] = useMutation(addSignupQuery, {
        refetchQueries: [
            'GetClassSignups' // Query name
        ],
    });

    const [deleteSignupMutation, { loading: deleteSignupLoading, error: deleteSignupError }] = useMutation(deleteSignupQuery, {
        refetchQueries: [
            'GetClassSignups' // Query name
        ],
    });

    const handleDeleteClick = (id) => {
        //Either signup or WL
        deleteSignupMutation({ variables: { id: id } });
    };

    const handleAddSignup = (e, reg) => {
        // TODO: Check for: being signed up
        const reg_id = reg.node.id;
        const event_id = event.id;
        addSignupMutation({ variables: { regId: reg_id, eventId: event_id, addToWL: addingToWL } });
        // TODO: chceck error return and show message
        setIsSelectStudentOpen(false);
    };

    const handleStudentsInputChange = async (e, newInputValue) => {
        if (newInputValue.length >= 3) {
            await refetchSelectStudent({ st: '%' + newInputValue + '%', cl: event.class });
            setStudentInputValue(newInputValue);
        } else {
            if (studentInputValue !== '') setStudentInputValue('');
        }
    };

    const resetStudentsInput = () => {
        setStudentInputValue('');
    };

    const searchOptions = (dataSelectStudent?.get_available_regs_for_event.edges && (studentInputValue !== '') && !loadingSelectStudent) ? dataSelectStudent?.get_available_regs_for_event.edges : [];

    return (
        <MDBox>
            <Card sx={{
                ...(isSmallScreen
                    ? { padding: "4px 0px 8px" }
                    : { padding: "4px 16px 8px" })
            }}>
                <MDBox ownerState={{
                    variant: "contained",
                    bgColor: "secondary",
                    coloredShadow: "secondary",
                }}
                    borderRadius={2}
                    sx={{ display: 'flex', width: '100%' }} pb={1} px={2} mt={-1} >
                    <MDTypography variant="h3" ownerState={{ color: "white" }}>
                        Students
                    </MDTypography>
                </MDBox>
                <MDBox component="table" sx={{ borderSpacing: "0px" }}>
                    <MDBox component="thead"
                        ownerState={{
                            variant: "contained",
                            bgColor: "light"
                        }}
                        borderRadius={2}>
                        <MDBox component="tr">
                            <MDBox component="td" {...{ colSpan: 3 }} px={1} py={1}>
                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="h5" ownerState={{ color: "dark", fontWeight: "bold" }}>
                                        Attendees
                                    </MDTypography>
                                    <MDButton
                                        ownerState={{
                                            variant: "text",
                                            color: "info",
                                            textTransform: "uppercase",
                                        }}
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setAddingToWL(false);
                                            if (event.capacity <= signedStudents.length) setIsConfirmOverCapacityOpen(true);
                                            else setIsSelectStudentOpen(true);
                                        }}
                                    >
                                        Add Student
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        </MDBox>
                        <MDBox component="tr">
                            <MDBox component="td" textAlign="left" px={1} py={1}>
                                <MDTypography variant="h5"
                                    ownerState={{
                                        color: "dark",
                                        fontWeight: "bold"
                                    }} >
                                    First Name
                                </MDTypography>
                            </MDBox>
                            <MDBox component="td" textAlign="left" px={1}>
                                <MDTypography variant="h5"
                                    ownerState={{
                                        color: "dark",
                                        fontWeight: "bold"
                                    }} >
                                    Last Name
                                </MDTypography>
                            </MDBox>
                            <MDBox component="td" textAlign="center" px={1}>
                                <MDTypography variant="h5"
                                    ownerState={{
                                        color: "dark",
                                        fontWeight: "bold"
                                    }} >
                                    Delete
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </MDBox>

                    <MDBox component="tbody">
                        {signedStudents?.map((signup) => (
                            <MDBox component="tr" key={signup.node.id} sx={{ '& > *': { borderBottom: 'solid 1px rgba(0, 0, 0, 0.08);' }, '& > *:focuse': { backgroundColor: "black" } }}>
                                <MDBox component="td" textAlign="left" px={1}>
                                    <MDTypography variant="h5"
                                        ownerState={{
                                            color: "inherit",
                                            fontWeight: "regular"
                                        }}>
                                        {signup.node.fs_registrations.fs_purchases.fs_students.FirstName}
                                    </MDTypography>
                                </MDBox>
                                <MDBox component="td" textAlign="left" px={1}>
                                    <MDTypography variant="h5" ownerState={{
                                        color: "inherit",
                                        fontWeight: "regular"
                                    }}>
                                        {signup.node.fs_registrations.fs_purchases.fs_students.LastName}
                                    </MDTypography>
                                </MDBox>
                                <MDBox textAlign="center" component="td" px={1}>
                                    <MDButton
                                        ownerState={{
                                            color: "primary",
                                            iconOnly: true,
                                        }}
                                        onClick={() => { handleDeleteClick(signup.node.id); }}
                                        aria-label="Delete"
                                    >
                                        <DeleteIcon />
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        ))}
                    </MDBox>

                </MDBox>
                {((event.capacity <= signedStudents?.length) || WLStudents?.length > 0) && (
                    <>
                        {/* ================== Wait List =============================== */}
                        <MDBox component="table" sx={{ borderSpacing: "0px" }}>
                            <MDBox component="thead"
                                ownerState={{
                                    variant: "contained",
                                    bgColor: "light"
                                }}
                                borderRadius={2}>
                                <MDBox component="tr">
                                    <MDBox component="td" {...{ colSpan: 3 }} px={1} py={1}>
                                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                            <MDTypography variant="h5" ownerState={{ color: "dark", fontWeight: "bold" }}>
                                                Wait List
                                            </MDTypography>
                                            <MDButton
                                                ownerState={{
                                                    variant: "text",
                                                    color: "info",
                                                    textTransform: "uppercase",
                                                }}
                                                startIcon={<AddIcon />}
                                                onClick={() => {
                                                    setAddingToWL(true);
                                                    setIsSelectStudentOpen(true);
                                                }}
                                            >
                                                Add to Wait List
                                            </MDButton>
                                        </MDBox>
                                    </MDBox>
                                </MDBox>
                                <MDBox component="tr">
                                    <MDBox component="td" textAlign="left" px={1} py={1}>
                                        <MDTypography variant="h5"
                                            ownerState={{
                                                color: "dark",
                                                fontWeight: "bold"
                                            }} >
                                            First Name
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox component="td" textAlign="left" px={1}>
                                        <MDTypography variant="h5"
                                            ownerState={{
                                                color: "dark",
                                                fontWeight: "bold"
                                            }} >
                                            Last Name
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox component="td" textAlign="center" px={1}>
                                        <MDTypography variant="h5"
                                            ownerState={{
                                                color: "dark",
                                                fontWeight: "bold"
                                            }} >
                                            Delete
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </MDBox>

                            <MDBox component="tbody">
                                {WLStudents?.map((signup) => (
                                    <MDBox component="tr" key={signup.node.id} sx={{ '& > *': { borderBottom: 'solid 1px rgba(0, 0, 0, 0.08);' }, '& > *:focuse': { backgroundColor: "black" } }}>
                                        <MDBox component="td" textAlign="left" px={1}>
                                            <MDTypography variant="h5"
                                                ownerState={{
                                                    color: "inherit",
                                                    fontWeight: "regular"
                                                }}>
                                                {signup.node.fs_registrations.fs_purchases.fs_students.FirstName}
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox component="td" textAlign="left" px={1}>
                                            <MDTypography variant="h5" ownerState={{
                                                color: "inherit",
                                                fontWeight: "regular"
                                            }}>
                                                {signup.node.fs_registrations.fs_purchases.fs_students.LastName}
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox textAlign="center" component="td" px={1}>
                                            <MDButton
                                                ownerState={{
                                                    color: "primary",
                                                    iconOnly: true,
                                                }}
                                                onClick={() => { handleDeleteClick(signup.node.id); }}
                                                aria-label="Delete"
                                            >
                                                <DeleteIcon />
                                            </MDButton>
                                        </MDBox>
                                    </MDBox>
                                ))}
                            </MDBox>
                        </MDBox>
                    </>)}
            </Card>





            {/* ================== Select student dialog =================== */}
            <Dialog
                maxWidth="md"
                fullWidth={true}
                open={isSelectStudentOpen}
                onClose={() => { setIsSelectStudentOpen(false) ; resetStudentsInput()}}>
                <MDTypography variant="h5" px={1} py={1}>Adding Attendee<CloseIcon onClick={() => { setIsSelectStudentOpen(false); resetStudentsInput()}} sx={{ position: "absolute", top: "10px", right: "10px" }} /></MDTypography>
                <DialogContent dividers>
                    <Autocomplete
                        fullWidth={true}
                        options={searchOptions}
                        onChange={handleAddSignup}
                        onInputChange={handleStudentsInputChange}
                        filterOptions={(x) => x}
                        getOptionLabel={(op: any) => {
                            return op.node.fs_purchases.fs_students.FirstName +
                                ' ' + op.node.fs_purchases.fs_students.LastName +
                                ' (' + op.node.fs_purchases.fs_students.Email + ')'
                        }}
                        renderInput={(params) => (
                            <MDInput {...params} label="Search" variant="outlined" />
                        )}
                    />
                </DialogContent>
            </Dialog>

            {/* ================== Confirm increase of capacity if over =================== */}
            <Dialog
                open={isConfirmOverCapacityOpen}
                onClose={() => setIsConfirmOverCapacityOpen(false)}>
                <MDTypography variant="h5" px={1} py={1}>Confirm Capacity Increase <CloseIcon onClick={() => setIsConfirmOverCapacityOpen(false)} sx={{ position: "absolute", top: "10px", right: "10px" }} /></MDTypography>
                <DialogContent dividers>
                    <DialogContentText id="overcapacity-alert-text">
                        <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>
                            The event is at max capacity. Add anyway?
                        </MDTypography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MDButton
                        ownerState={{
                            variant: "gradient",
                            color: "dark",
                            textTransform: "none",
                        }}
                        onClick={() => setIsConfirmOverCapacityOpen(false)}
                        autoFocus>Cancel</MDButton>
                    {/*TODO!!! Propagate capacity increase up to parent components*/}
                    <MDButton
                        ownerState={{
                            variant: "gradient",
                            color: "info",
                            textTransform: "none",
                        }}
                        onClick={() => { setIsConfirmOverCapacityOpen(false); setIsSelectStudentOpen(true); }}>
                        Yes
                    </MDButton>
                </DialogActions>
            </Dialog>

        </MDBox>
    )
}


export default EditEventAttendees;
